import { Calendar, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { EmptyState } from '@/components/ds/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '@/components/ui/Select'
import {
  DEFAULT_VIEW,
  factoryOptions,
  filterCounts,
  visibleCards,
  type ChannelCardModel,
  type ChannelFilter,
  type ChannelSort,
} from '@/features/scheduler/channelCards'
import { ChannelCard } from '@/features/scheduler/components/ChannelCard'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { cn } from '@/lib/cn'
import styles from './ChannelBoard.module.scss'

const FILTERS: { value: ChannelFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'attention', label: 'Com pendência' },
]

const SORTS = [
  { value: 'name' as const, label: 'Nome' },
  { value: 'next' as const, label: 'Próxima geração' },
]

// O Radix Select não aceita valor vazio.
const ALL_FACTORIES = '__all'

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

interface ChannelBoardProps {
  cards: readonly ChannelCardModel[]
  selectedSlug: string | undefined
  now: Date
  onEdit: (slug: string) => void
  onDrafts: (slug: string) => void
  onDetails: (slug: string) => void
}

/**
 * A grade de canais do Scheduler, com a busca (atalho ``/``), os filtros e a
 * ordem. A ordem padrão é por nome, para cada canal ficar sempre no mesmo
 * lugar; o que pede atenção aparece pelos selos e pelo filtro "Com
 * pendência", não pela posição.
 */
export function ChannelBoard({ cards, selectedSlug, now, onEdit, onDrafts, onDetails }: ChannelBoardProps) {
  const [query, setQuery] = useState(DEFAULT_VIEW.query)
  const [filter, setFilter] = useState<ChannelFilter>(DEFAULT_VIEW.filter)
  const [factory, setFactory] = useState(DEFAULT_VIEW.factory)
  const [sort, setSort] = useLocalStorage<ChannelSort>('prism.scheduler.sort', DEFAULT_VIEW.sort)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  const shown = useMemo(
    () => visibleCards(cards, { query, filter, factory, sort }),
    [cards, query, filter, factory, sort],
  )
  const counts = useMemo(() => filterCounts(cards), [cards])
  const factories = useMemo(() => factoryOptions(cards), [cards])
  const filtered = query !== '' || filter !== 'all' || factory !== ''

  const clear = () => {
    setQuery('')
    setFilter('all')
    setFactory('')
  }

  return (
    <section className={styles.board} aria-label="Canais">
      <div className={styles.toolbar}>
        <Input
          ref={searchRef}
          className={styles.search}
          type="search"
          placeholder="Buscar canal…"
          aria-label="Buscar canal"
          value={query}
          leadingIcon={<Search />}
          trailingIcon={query ? undefined : <kbd className={styles.kbd}>/</kbd>}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setQuery('')
          }}
        />
        <div className={styles.filters} role="group" aria-label="Filtrar canais">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={cn(styles.chip, filter === f.value && styles.chipOn)}
              aria-pressed={filter === f.value}
              onClick={() => {
                setFilter(f.value)
              }}
            >
              {f.label}
              <span className={styles.count}>{counts[f.value]}</span>
            </button>
          ))}
          {factories.length > 1 ? (
            <SelectRoot
              value={factory || ALL_FACTORIES}
              onValueChange={(v) => {
                setFactory(v === ALL_FACTORIES ? '' : v)
              }}
            >
              <SelectTrigger className={cn(styles.factory, factory && styles.chipOn)} aria-label="Filtrar por factory">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FACTORIES}>Todas as factories</SelectItem>
                {factories.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          ) : null}
        </div>
        <div className={styles.sort}>
          <span className={styles.sortLabel} aria-hidden>
            Ordenar
          </span>
          <Segmented size="sm" aria-label="Ordenar canais" options={SORTS} value={sort} onValueChange={setSort} />
        </div>
      </div>

      {cards.length === 0 ? (
        <EmptyState title="Nenhum canal configurado" icon={<Calendar />} />
      ) : shown.length === 0 ? (
        <EmptyState
          title="Nenhum canal com esses filtros"
          icon={<Search />}
          action={
            <Button variant="secondary" onClick={clear}>
              Limpar filtros
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {shown.map((card) => (
            <ChannelCard
              key={card.slug}
              card={card}
              selected={card.slug === selectedSlug}
              now={now}
              onEdit={() => {
                onEdit(card.slug)
              }}
              onDrafts={() => {
                onDrafts(card.slug)
              }}
              onDetails={() => {
                onDetails(card.slug)
              }}
            />
          ))}
        </div>
      )}
      {filtered && shown.length > 0 ? (
        <p className={styles.result} role="status">
          {shown.length} de {cards.length} canais ·{' '}
          <button type="button" className={styles.link} onClick={clear}>
            limpar filtros
          </button>
        </p>
      ) : null}
    </section>
  )
}
